import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import type { ReactNode, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import './primitives.css';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: DialogSize;
  /** If false, clicking the backdrop will not close the dialog. */
  closeOnBackdrop?: boolean;
  /** Backdrop clicks this many pixels from the dialog edge are ignored. */
  backdropClickSafeMargin?: number;
  /** If false, pressing Escape will not close the dialog. */
  closeOnEscape?: boolean;
  /** Hide the header close button (e.g. for forced flows like must-change-password). */
  hideCloseButton?: boolean;
  /**
   * When true, the dialog can be dragged by its header to reveal content
   * underneath. The drag is clamped so the dialog can never be lost off-screen,
   * and the position re-centers each time the dialog opens.
   */
  draggable?: boolean;
  /**
   * When true, the dialog can be resized from its bottom-right corner
   * (native CSS resize, clamped to the viewport). The size resets each open,
   * like the drag position.
   */
  resizable?: boolean;
  /**
   * When provided, the header shows a minimize button. The dialog itself only
   * reports the intent — the caller owns what "minimized" means (e.g. docking
   * to an OptimizeDock chip).
   */
  onMinimize?: () => void;
  className?: string;
  /** Optional id for the backdrop element. */
  id?: string;
  /** Optional aria-label when there's no visible title. */
  ariaLabel?: string;
}

/** Keep at least this many px of the dialog on screen while dragging. */
const DRAG_KEEP_VISIBLE = 64;
/** Minimum gap between a floating dialog and the viewport edge when centered. */
const DIALOG_MARGIN = 16;

interface DragState {
  startX: number;
  startY: number;
  baseLeft: number;
  baseTop: number;
  width: number;
}

/**
 * Leitmotif Dialog.
 *
 * Preserves the behavior required by CLAUDE.md RULE 6:
 *   - portals to document.body (isolated from DragDropContext, etc.)
 *   - no transition delay between open state changes
 *
 * Call onClose() before any async work to avoid stuck dialogs.
 */
export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  backdropClickSafeMargin = 0,
  closeOnEscape = true,
  hideCloseButton = false,
  draggable = false,
  resizable = false,
  onMinimize,
  className,
  id,
  ariaLabel,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const floating = draggable || resizable;
  // Floating dialogs hold an explicit top-left corner (px). Non-floating
  // dialogs stay null and are centered by the backdrop's flexbox.
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef<DragState | null>(null);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, handleClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Focus the dialog when opened (basic focus management).
  useEffect(() => {
    if (isOpen) dialogRef.current?.focus();
  }, [isOpen]);

  // Each open centers the dialog once, computed from its measured size into an
  // explicit top-left. Holding a size-independent corner (rather than
  // re-centering) is what makes a resize grow only down and to the right; a
  // prior drag never carries over. Runs before paint, so there is no visible
  // jump from the initial centered box.
  useLayoutEffect(() => {
    if (!isOpen || !floating) {
      setPos(null);
      return;
    }
    const el = dialogRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      left: Math.max(DIALOG_MARGIN, (window.innerWidth - rect.width) / 2),
      top: Math.max(DIALOG_MARGIN, (window.innerHeight - rect.height) / 2),
    });
  }, [isOpen, floating]);

  const onHeaderMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!draggable) return;
    if ((e.target as HTMLElement).closest('.lm-dialog__close, .lm-dialog__minimize')) return;
    const el = dialogRef.current;
    if (!el || !pos) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseLeft: pos.left,
      baseTop: pos.top,
      width: rect.width,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: globalThis.MouseEvent) => {
      const s = dragState.current;
      if (!s) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.min(
        Math.max(s.baseLeft + (e.clientX - s.startX), DRAG_KEEP_VISIBLE - s.width),
        vw - DRAG_KEEP_VISIBLE,
      );
      const top = Math.min(
        Math.max(s.baseTop + (e.clientY - s.startY), 0),
        vh - DRAG_KEEP_VISIBLE,
      );
      setPos({ left, top });
    };
    const onUp = () => {
      setIsDragging(false);
      dragState.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  if (!isOpen) return null;

  const onBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!closeOnBackdrop) return;
    if (e.target !== e.currentTarget) return;

    const safeMargin = Math.max(0, backdropClickSafeMargin);
    if (safeMargin > 0 && dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      const inSafeArea =
        e.clientX >= rect.left - safeMargin
        && e.clientX <= rect.right + safeMargin
        && e.clientY >= rect.top - safeMargin
        && e.clientY <= rect.bottom + safeMargin;
      if (inSafeArea) return;
    }

    handleClose();
  };

  const dialogClasses = [
    'lm-dialog',
    `lm-dialog--${size}`,
    isDragging ? 'lm-dialog--dragging' : '',
    resizable ? 'lm-dialog--resizable' : '',
    className,
  ].filter(Boolean).join(' ');

  const floatingStyle = floating && pos
    ? { position: 'fixed' as const, left: pos.left, top: pos.top, margin: 0 }
    : undefined;

  return createPortal(
    <div className="lm-dialog-backdrop" id={id} onClick={onBackdropClick} role="presentation">
      <div
        ref={dialogRef}
        className={dialogClasses}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
        style={floatingStyle}
      >
        {(title || !hideCloseButton) && (
          <div
            className={`lm-dialog__header${draggable ? ' lm-dialog__header--draggable' : ''}`}
            onMouseDown={onHeaderMouseDown}
          >
            <div className="lm-dialog__heading">
              {draggable && (
                <span className="lm-dialog__grip" aria-hidden="true">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                    <circle cx="2" cy="3" r="1.3" />
                    <circle cx="8" cy="3" r="1.3" />
                    <circle cx="2" cy="8" r="1.3" />
                    <circle cx="8" cy="8" r="1.3" />
                    <circle cx="2" cy="13" r="1.3" />
                    <circle cx="8" cy="13" r="1.3" />
                  </svg>
                </span>
              )}
              <div className="lm-dialog__title">{title}</div>
            </div>
            <div className="lm-dialog__headbtns">
              {onMinimize && (
                <button
                  type="button"
                  className="lm-dialog__close lm-dialog__minimize"
                  onClick={onMinimize}
                  aria-label="Minimize dialog"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M4 11h8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              )}
              {!hideCloseButton && (
              <button
                type="button"
                className="lm-dialog__close"
                onClick={handleClose}
                aria-label="Close dialog"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              )}
            </div>
          </div>
        )}
        <div className="lm-dialog__body">{children}</div>
        {footer && <div className="lm-dialog__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
